import { useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import TwinButterfly from "@/components/TwinButterfly";
import ShardImage from "@/components/ShardImage";

const XPS_URL = "https://trifitxps.com";
const R = ({ children, d = 0, c = "" }) => (
  <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay: d, ease: [0.16, 1, 0.3, 1] }} className={c}>{children}</motion.div>
);
const Cta = ({ testid, c = "" }) => (
  <a href={XPS_URL} target="_blank" rel="noopener noreferrer" data-testid={testid} className={`inline-block bg-[#101014] px-9 py-4 font-mono text-[11px] tracking-[0.16em] text-white transition-colors duration-300 hover:bg-signal ${c}`}>GET TRIFIT XPS</a>
);

const DIMS = [
  {
    id: "rest",
    label: "REST",
    note: "Sleep, HRV, resting HR.",
    y: "22%",
  },
  {
    id: "effort",
    label: "EFFORT",
    note: "Internal and external load.",
    y: "42%",
  },
  {
    id: "readiness",
    label: "READINESS",
    note: "Sport-specific. Predicts injury risk.",
    y: "62%",
  },
  {
    id: "sharpness",
    label: "SHARPNESS",
    note: "Peak physical and mental output.",
    y: "82%",
  },
];

const SCEN = [
  { id: "a", label: "OPTION A · LOWER LOAD", path: "M20 150 C120 140 220 120 380 96", note: "Freshness rises." },
  { id: "b", label: "OPTION B · MAINTAIN", path: "M20 150 C120 148 220 142 380 132", note: "State holds." },
  { id: "c", label: "OPTION C · HIGHER LOAD", path: "M20 150 C120 162 220 176 380 188", note: "Short-term dip, longer gain." },
];

const LOOP = [
  ["SIGNALS", "Wearables, GPS"],
  ["UNDERSTAND", "The athlete twin"],
  ["SIMULATE", "Possible responses"],
  ["DECIDE", "The team calls it"],
  ["PERFORM", "The decision runs"],
  ["LEARN", "The response returns"],
];

/* --------------------------------------------------------------- */
/* Section 02 · Decision problem: 6 questions, each with an example  */
/* Each question opens an in-place chart + short athlete state note  */
/* --------------------------------------------------------------- */
const QUESTIONS = [
  {
    id: "q1",
    q: "What changed?",
    metric: "HRV · 7D",
    unit: "ms",
    kind: "drop",
    // simple monotonically decreasing series
    series: [72, 71, 69, 66, 64, 61, 58],
    domainY: [50, 80],
    delta: "−14 ms",
    state:
      "Rower · 24y. HRV drifting down.",
  },
  {
    id: "q2",
    q: "Why?",
    metric: "SLEEP DEBT · 7D",
    unit: "h",
    kind: "growth",
    series: [0.4, 0.9, 1.6, 2.4, 3.1, 3.8, 4.6],
    domainY: [0, 6],
    delta: "+4.2 h debt",
    state:
      "Sleep collapsed 7.8h to 6.1h.",
  },
  {
    id: "q3",
    q: "Are they adapting?",
    metric: "PEAK POWER · 4W",
    unit: "W/kg",
    kind: "rise",
    series: [5.1, 5.15, 5.2, 5.28, 5.34, 5.39, 5.47],
    domainY: [4.8, 5.6],
    delta: "+0.37 W/kg",
    state:
      "Cyclist · 28y. Peak power still climbing.",
  },
  {
    id: "q4",
    q: "What am I missing?",
    metric: "L/R CONTACT · 14D",
    unit: "ms",
    kind: "asymmetry",
    seriesL: [188, 186, 187, 189, 186, 188, 187],
    seriesR: [190, 192, 195, 199, 204, 208, 213],
    domainY: [175, 220],
    delta: "R +12% vs L",
    state:
      "Sprinter · 22y. Right-side contact rising.",
  },
  {
    id: "q5",
    q: "What if we change this?",
    metric: "READINESS · 72H",
    unit: "%",
    kind: "branching",
    // three branches: lower / maintain / higher · all starting from 62
    branchA: [62, 66, 71, 78],
    branchB: [62, 63, 64, 65],
    branchC: [62, 60, 57, 52],
    domainY: [45, 85],
    delta: "±26 pts spread",
    state:
      "Three sessions, three trajectories.",
  },
  {
    id: "q6",
    q: "What next?",
    metric: "PEAK · 21D",
    unit: "index",
    kind: "peak",
    series: [42, 48, 55, 61, 68, 74, 80, 86, 91, 95, 98, 100, 98, 94, 88, 82, 75, 68, 60, 52, 45],
    domainY: [30, 105],
    delta: "PEAK · DAY 12",
    state:
      "Peak form lands on race day.",
  },
];

const chartWidth = 640;
const chartHeight = 180;
const chartPadX = 24;
const chartPadY = 20;

const toPoints = (arr, [minY, maxY]) => {
  const n = arr.length;
  const w = chartWidth - chartPadX * 2;
  const h = chartHeight - chartPadY * 2;
  return arr.map((v, i) => {
    const x = chartPadX + (i * w) / (n - 1);
    const y = chartPadY + h - ((v - minY) / (maxY - minY)) * h;
    return [x, y];
  });
};

const pathFrom = (pts) =>
  pts.reduce(
    (acc, [x, y], i) => acc + (i === 0 ? `M${x},${y}` : ` L${x},${y}`),
    ""
  );

const QuestionChart = ({ item }) => {
  const draw = { pathLength: 1, opacity: 1 };
  const start = { pathLength: 0, opacity: 0.4 };
  const transition = { duration: 1.4, ease: [0.16, 1, 0.3, 1] };

  const commonAxes = (
    <>
      <line
        x1={chartPadX}
        y1={chartHeight - chartPadY}
        x2={chartWidth - chartPadX}
        y2={chartHeight - chartPadY}
        stroke="#2A2026"
      />
      <line
        x1={chartPadX}
        y1={chartPadY}
        x2={chartPadX}
        y2={chartHeight - chartPadY}
        stroke="#2A2026"
      />
    </>
  );

  if (item.kind === "asymmetry") {
    const ptsL = toPoints(item.seriesL, item.domainY);
    const ptsR = toPoints(item.seriesR, item.domainY);
    return (
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full">
        {commonAxes}
        <motion.path
          d={pathFrom(ptsL)}
          fill="none"
          stroke="#A19A9D"
          strokeWidth="1.5"
          strokeLinecap="round"
          initial={start}
          animate={draw}
          transition={transition}
        />
        <motion.path
          d={pathFrom(ptsR)}
          fill="none"
          stroke="#D2378B"
          strokeWidth="2"
          strokeLinecap="round"
          initial={start}
          animate={draw}
          transition={{ ...transition, delay: 0.2 }}
        />
        <text
          x={chartWidth - chartPadX}
          y={chartPadY + 4}
          fontSize="9"
          fontFamily="JetBrains Mono"
          fill="#D2378B"
          textAnchor="end"
        >
          RIGHT
        </text>
        <text
          x={chartWidth - chartPadX}
          y={chartPadY + 16}
          fontSize="9"
          fontFamily="JetBrains Mono"
          fill="#A19A9D"
          textAnchor="end"
        >
          LEFT
        </text>
      </svg>
    );
  }

  if (item.kind === "branching") {
    const a = toPoints(item.branchA, item.domainY);
    const b = toPoints(item.branchB, item.domainY);
    const c = toPoints(item.branchC, item.domainY);
    return (
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full">
        {commonAxes}
        <motion.path
          d={pathFrom(a)}
          fill="none"
          stroke="#D2378B"
          strokeWidth="2"
          initial={start}
          animate={draw}
          transition={transition}
        />
        <motion.path
          d={pathFrom(b)}
          fill="none"
          stroke="#A19A9D"
          strokeWidth="1.5"
          strokeDasharray="4 4"
          initial={start}
          animate={draw}
          transition={{ ...transition, delay: 0.2 }}
        />
        <motion.path
          d={pathFrom(c)}
          fill="none"
          stroke="#6E676A"
          strokeWidth="1.5"
          strokeDasharray="2 4"
          initial={start}
          animate={draw}
          transition={{ ...transition, delay: 0.4 }}
        />
        <text
          x={chartWidth - chartPadX - 4}
          y={a[a.length - 1][1] + 3}
          fontSize="9"
          fontFamily="JetBrains Mono"
          fill="#D2378B"
          textAnchor="end"
        >
          LOWER LOAD
        </text>
        <text
          x={chartWidth - chartPadX - 4}
          y={b[b.length - 1][1] + 3}
          fontSize="9"
          fontFamily="JetBrains Mono"
          fill="#A19A9D"
          textAnchor="end"
        >
          MAINTAIN
        </text>
        <text
          x={chartWidth - chartPadX - 4}
          y={c[c.length - 1][1] + 3}
          fontSize="9"
          fontFamily="JetBrains Mono"
          fill="#6E676A"
          textAnchor="end"
        >
          HIGHER LOAD
        </text>
      </svg>
    );
  }

  // linear kinds: drop / growth / rise / peak
  const pts = toPoints(item.series, item.domainY);
  const stroke = item.kind === "rise" || item.kind === "peak" ? "#D2378B" : "#D2378B";
  // annotate peak
  let peakIdx = -1;
  if (item.kind === "peak") {
    peakIdx = item.series.indexOf(Math.max(...item.series));
  }
  return (
    <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full">
      {commonAxes}
      {/* gradient area fill */}
      <defs>
        <linearGradient id={`grad-${item.id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#D2378B" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#D2378B" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d={`${pathFrom(pts)} L${pts[pts.length - 1][0]},${chartHeight - chartPadY} L${pts[0][0]},${chartHeight - chartPadY} Z`}
        fill={`url(#grad-${item.id})`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.4 }}
      />
      <motion.path
        d={pathFrom(pts)}
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        strokeLinecap="round"
        initial={start}
        animate={draw}
        transition={transition}
      />
      {pts.map(([x, y], i) => (
        <motion.circle
          key={i}
          cx={x}
          cy={y}
          r={i === peakIdx ? 4 : 1.8}
          fill={i === peakIdx ? "#D2378B" : "#FFFFFF"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.8 + i * 0.04 }}
        />
      ))}
      {item.kind === "peak" && peakIdx >= 0 && (
        <motion.text
          x={pts[peakIdx][0]}
          y={pts[peakIdx][1] - 10}
          fontSize="9"
          fontFamily="JetBrains Mono"
          fill="#D2378B"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
        >
          RACE DAY
        </motion.text>
      )}
    </svg>
  );
};

const QuestionCard = ({ item, i, open, onToggle }) => (
  <div
    data-testid={`xps-question-${item.id}`}
    className={`overflow-hidden border-l-2 transition-colors duration-500 ${
      open ? "border-signal" : i % 3 === 0 ? "border-signal/60" : "border-line"
    }`}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`xps-question-toggle-${item.id}`}
      className="flex w-full items-center justify-between gap-4 py-3 pl-4 pr-2 text-left transition-colors duration-300 hover:text-signal"
    >
      <span className="font-display text-lg font-light md:text-2xl">
        {item.q}
      </span>
      <motion.span
        animate={{ rotate: open ? 45 : 0 }}
        transition={{ duration: 0.3 }}
        className={`font-mono text-2xl leading-none ${open ? "text-signal" : "text-ash"}`}
        aria-hidden="true"
      >
        +
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="body"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <div
            data-testid={`xps-question-panel-${item.id}`}
            className="mx-1 my-4 rounded-xl border border-line bg-ink-2/60 p-5 md:p-6"
          >
            <div className="flex items-center justify-between gap-4">
              <p className="font-mono text-[10px] tracking-[0.22em] text-ash">
                {item.metric}
              </p>
              <p className="font-mono text-[10px] tracking-[0.2em] text-signal">
                {item.delta}
              </p>
            </div>
            <div className="mt-3">
              <QuestionChart item={item} />
            </div>
            <p
              data-testid={`xps-question-note-${item.id}`}
              className="mt-4 max-w-2xl text-sm leading-relaxed text-fog md:text-base"
            >
              {item.state}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* --------------------------------------------------------------------- */
/* Section 02 · pinned scroll-driven decision problem                       */
/* Each question opens automatically as the reader scrolls through it       */
/* --------------------------------------------------------------------- */
const ProblemPinned = ({ questions, openQ, setOpenQ }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const n = questions.length;
    const t = Math.min(1, Math.max(0, (v - 0.05) / 0.9));
    const idx = Math.min(n - 1, Math.floor(t * n));
    const next = questions[idx].id;
    setOpenQ((cur) => (cur === next ? cur : next));
  });
  const railFill = useTransform(scrollYProgress, [0.05, 0.95], ["0%", "100%"]);
  const activeIdx = Math.max(0, questions.findIndex((q) => q.id === openQ));

  return (
    <section
      ref={ref}
      data-testid="xps-problem"
      className="relative"
      style={{ height: "440vh" }}
    >
      <div className="sticky top-0 flex h-screen items-center overflow-hidden px-6 md:px-12">
        <div className="mx-auto grid w-full max-w-[1500px] items-center gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
              02 · THE DECISION PROBLEM
            </p>
            <h2 className="mt-3 font-display text-2xl font-light leading-tight md:text-4xl">Context is <span className="text-signal">fragmented</span>.</h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-fog">
              Across specialists and moments.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <p className="font-mono text-[9px] tracking-[0.24em] text-ash">
                {String(activeIdx + 1).padStart(2, "0")} / {String(questions.length).padStart(2, "0")}
              </p>
              <div className="relative h-px w-32 bg-line">
                <motion.div
                  style={{ width: railFill }}
                  className="absolute left-0 top-0 h-px origin-left bg-signal"
                />
              </div>
              <p className="font-mono text-[9px] tracking-[0.24em] text-signal">
                {questions[activeIdx].q.toUpperCase()}
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="flex flex-col gap-1 rounded-2xl border border-line bg-ink-2/40 p-2 md:p-3">
              {questions.map((item, i) => (
                <QuestionCard
                  key={item.id}
                  item={item}
                  i={i}
                  open={openQ === item.id}
                  onToggle={() =>
                    setOpenQ((cur) => (cur === item.id ? null : item.id))
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --------------------------------------------------------------------- */
/* Section 03 · pinned, scroll-driven segmented athlete                    */
/* --------------------------------------------------------------------- */
const UnderstandPinned = ({ dims }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const n = dims.length;
    // occupy the middle 80% of the scroll for the transitions
    const t = Math.min(1, Math.max(0, (v - 0.1) / 0.8));
    const next = Math.min(n - 1, Math.floor(t * n));
    if (next !== active) setActive(next);
  });
  const barY = useTransform(scrollYProgress, [0.1, 0.9], ["0%", "100%"]);

  return (
    <section
      ref={ref}
      data-testid="xps-understand"
      className="relative bg-ink-2/50"
      style={{ height: "300vh" }}
    >
      <div className="sticky top-0 flex h-screen items-center overflow-hidden px-6 md:px-12">
        <div className="mx-auto grid w-full max-w-[1500px] items-center gap-10 lg:grid-cols-12">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
              03 · UNDERSTAND · DIGITAL TWIN
            </p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-light leading-tight md:text-5xl">
              Know who you&apos;re making the decision{" "}
              <span className="text-signal">for</span>.
            </h2>
            <p className="mt-6 font-mono text-[10px] tracking-[0.24em] text-signal">
              FOUR SCORES. ONE ATHLETE.
            </p>

            {/* dim list · the currently active row swaps */}
            <div className="mt-6 space-y-3 md:mt-8">
              {dims.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  aria-current={i === active}
                  data-testid={`xps-dim-${s.id}`}
                  className={`block w-full border-l-2 pl-4 text-left transition-all duration-500 ${
                    i === active
                      ? "border-signal opacity-100"
                      : "border-line opacity-40"
                  }`}
                >
                  <p
                    className={`font-mono text-[10px] tracking-[0.2em] ${
                      i === active ? "text-signal" : "text-ash"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")} · {s.label}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-fog">
                    {s.note}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Right athlete image with 4 segments */}
          <div className="lg:col-span-7">
            <div className="relative mx-auto aspect-[3/4] w-full max-w-md overflow-hidden rounded-xl border border-line lg:max-w-lg">
              <img
                src="/assets/xps_athlete.jpg"
                alt="Athlete decomposed into performance dimensions"
                className="absolute inset-0 h-full w-full object-cover object-[62%_center]"
              />
              {/* dim overlay */}
              <div className="absolute inset-0 bg-ink/45" />

              {/* segment overlays · 4 equal horizontal bands */}
              {dims.map((s, i) => {
                const top = (i * 100) / dims.length;
                const heightPct = 100 / dims.length;
                const isActive = i === active;
                return (
                  <div
                    key={s.id}
                    data-testid={`xps-segment-${s.id}`}
                    style={{
                      top: `${top}%`,
                      height: `${heightPct}%`,
                    }}
                    className={`absolute left-0 right-0 border-t transition-all duration-700 ${
                      isActive
                        ? "border-signal bg-signal/20 shadow-[inset_0_0_120px_rgba(210,55,139,0.25)]"
                        : "border-white/20"
                    }`}
                  >
                    <div className="absolute left-3 top-3 flex items-center gap-2">
                      <span
                        className={`h-px transition-all duration-500 ${
                          isActive ? "w-8 bg-signal" : "w-3 bg-white/50"
                        }`}
                      />
                      <span
                        className={`font-mono text-[9px] tracking-[0.22em] ${
                          isActive ? "text-signal" : "text-white/80"
                        }`}
                      >
                        {s.label}
                      </span>
                    </div>
                    {isActive && (
                      <motion.div
                        layoutId="xps-active-frame"
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-signal"
                      />
                    )}
                  </div>
                );
              })}
            </div>
            {/* Progress bar */}
            <div className="mx-auto mt-5 flex max-w-md items-center gap-3 lg:max-w-lg">
              <p className="font-mono text-[9px] tracking-[0.24em] text-ash">
                {String(active + 1).padStart(2, "0")} / {String(dims.length).padStart(2, "0")}
              </p>
              <div className="relative h-px flex-1 bg-line">
                <motion.div
                  style={{ width: barY }}
                  className="absolute left-0 top-0 h-px origin-left bg-signal"
                />
              </div>
              <p className="font-mono text-[9px] tracking-[0.24em] text-signal">
                {dims[active].label}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --------------------------------------------------------------------- */
/* Section 06 · pinned scroll-driven decision loop highlight               */
/* --------------------------------------------------------------------- */
const LoopScrollTrack = ({ items }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    // Middle 85% of the scroll journey drives the 6 highlights
    const t = Math.min(1, Math.max(0, (v - 0.05) / 0.85));
    const next = Math.min(items.length - 1, Math.floor(t * items.length));
    setActive((cur) => (cur === next ? cur : next));
  });

  const railFill = useTransform(scrollYProgress, [0.05, 0.9], ["0%", "100%"]);

  return (
    <section
      ref={ref}
      data-testid="xps-loop"
      className="relative"
      style={{ height: "260vh" }}
    >
      <div className="sticky top-0 flex h-screen items-center overflow-hidden px-6 md:px-12">
        <div className="mx-auto w-full max-w-[1500px]">
          <div>
            <p className="font-mono text-[11px] tracking-[0.28em] text-ash">
              06 · THE WINNING DECISION LOOP
            </p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-light leading-tight md:text-5xl">Every call makes the next one <span className="text-signal">better</span>.</h2>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-fog md:text-base">Signals become decisions. Decisions become new signals.</p>
          </div>

          {/* Progress rail */}
          <div className="mt-8 flex items-center gap-4">
            <p className="font-mono text-[9px] tracking-[0.24em] text-ash">
              {String(active + 1).padStart(2, "0")} / 06
            </p>
            <div className="relative h-px flex-1 bg-line">
              <motion.div
                style={{ width: railFill }}
                className="absolute left-0 top-0 h-px origin-left bg-signal"
              />
            </div>
            <p className="font-mono text-[9px] tracking-[0.24em] text-signal">
              {items[active][0]}
            </p>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            {items.map(([t, d], i) => (
              <div
                key={t}
                data-testid={`xps-loop-${t.toLowerCase()}`}
                aria-current={i === active}
                className={`relative h-full rounded-xl border p-5 transition-all duration-500 ${
                  i === active
                    ? "border-signal bg-signal/10 shadow-[0_0_0_1px_rgba(210,55,139,0.35)]"
                    : "border-line bg-ink-2/50"
                }`}
              >
                <p
                  className={`font-mono text-[9px] tracking-[0.2em] transition-colors duration-500 ${
                    i === active ? "text-signal" : "text-ash"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-3 font-display text-xl font-light text-white">
                  {t}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-fog">{d}</p>
                {i === active && (
                  <motion.span
                    layoutId="xps-loop-active"
                    transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                    className="pointer-events-none absolute inset-x-4 -bottom-0.5 h-px bg-signal"
                  />
                )}
              </div>
            ))}
          </div>
          <p className="mt-8 text-center font-mono text-[10px] tracking-[0.24em] text-ash">
            LEARN <span className="text-signal">↻</span> FEEDS BACK INTO UNDERSTAND · THE LOOP IS CLOSED
          </p>
        </div>
      </div>
    </section>
  );
};

/* --- XPS + Trifinity butterfly waypoints ------------------------------ */
const XPS_STOPS = [0, 0.14, 0.28, 0.42, 0.56, 0.72, 0.86, 1];
const XPS_XS =    ["78%", "12%", "84%", "14%", "82%", "16%", "84%", "20%"];
const XPS_YS =    ["30%", "62%", "22%", "58%", "26%", "60%", "28%", "62%"];

const TrifitXps = () => {
  const [scen, setScen] = useState("b");
  const [openQ, setOpenQ] = useState("q1");
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 900], [0, 140]);
  return (
    <main data-testid="xps-page" className="relative z-10 text-white">
      <TwinButterfly stops={XPS_STOPS} xs={XPS_XS} ys={XPS_YS} />
      {/* 01 HERO */}
      <section data-testid="xps-hero" className="relative flex min-h-screen items-center overflow-hidden bg-[#F4F2EF] pt-20 text-[#17151A]">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <ShardImage
            src="/assets/xps_athlete.jpg"
            alt="Athlete sprinting at full effort"
            data-testid="xps-hero-shard"
            objectPosition="75% 50%"
            strips={16}
            angle={62}
            offset={160}
            duration={3.0}
            delay={0.2}
            className="h-full w-full"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#F4F2EF] via-[#F4F2EF]/80 to-transparent" />
        {/* twin overlay */}
        <div className="absolute right-[6%] top-[24%] hidden lg:block" aria-hidden="true">
          {["SHARPNESS", "READINESS", "EFFORT", "REST"].map((t, i) => (
            <motion.p key={t} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 + i * 0.2 }} className="mb-5 border-l border-signal/60 pl-3 font-mono text-[10px] tracking-[0.2em] text-fog">{t}</motion.p>
          ))}
        </div>
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 md:px-12">
          <p className="font-mono text-[11px] tracking-[0.28em] text-[#17151A]/50">TRIFIT XPS</p>
          <h1 data-testid="xps-hero-headline" className="mt-8 max-w-2xl font-display text-5xl font-light leading-[1.02] sm:text-6xl lg:text-7xl">Make winning <span className="text-signal">decisions</span>.</h1>
          <p className="mt-8 max-w-md text-base leading-relaxed text-[#17151A]/70">Winning is decided before competition.</p>
          <div className="mt-10"><Cta testid="xps-hero-cta" /></div>
        </div>
      </section>

      {/* 02 PROBLEM · pinned scroll-driven question reveal */}
      <ProblemPinned questions={QUESTIONS} openQ={openQ} setOpenQ={setOpenQ} />

      {/* 03 UNDERSTAND · pinned, scroll-driven segmented athlete */}
      <UnderstandPinned dims={DIMS} />

      {/* 04 ANTICIPATE · simulator */}
      <section data-testid="xps-anticipate" className="px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-[1500px]">
          <R>
            <p className="font-mono text-[11px] tracking-[0.28em] text-ash">04 · ANTICIPATE · SIMULATOR</p>
            <h2 className="mt-4 max-w-2xl font-display text-3xl font-light leading-tight md:text-5xl">See the call before you <span className="text-signal">make it</span>.</h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-fog">Compare the responses. Weigh the trade-offs.</p>
          </R>
          <R d={0.15}>
            <div className="mt-12 rounded-2xl border border-line bg-ink-2/50 p-6 md:p-10">
              <p className="font-mono text-[10px] tracking-[0.22em] text-ash">DECISION: TOMORROW&apos;S TRAINING LOAD</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {SCEN.map((s) => (
                  <button key={s.id} data-testid={`xps-scenario-${s.id}`} onClick={() => setScen(s.id)} className={`rounded-full border px-5 py-3 font-mono text-[10px] tracking-[0.16em] transition-colors duration-300 ${scen === s.id ? "border-signal bg-signal text-white" : "border-line text-fog hover:border-fog"}`}>{s.label}</button>
                ))}
              </div>
              <svg viewBox="0 0 400 240" className="mt-8 w-full max-w-3xl" aria-hidden="true">
                <line x1="20" y1="20" x2="20" y2="220" stroke="#6E676A" strokeOpacity="0.15" />
                <line x1="20" y1="220" x2="390" y2="220" stroke="#6E676A" strokeOpacity="0.15" />
                {SCEN.map((s) => (
                  <path key={s.id} d={s.path} stroke={scen === s.id ? "#D2378B" : "#A19A9D"} strokeOpacity={scen === s.id ? 1 : 0.15} strokeWidth={scen === s.id ? 2 : 1} fill="none" style={{ transition: "all 0.5s" }} />
                ))}
                <circle cx="20" cy="150" r="5" fill="#A19A9D" />
                <text x="30" y="145" fontSize="10" fontFamily="JetBrains Mono" fill="#A19A9D" opacity="0.6">CURRENT STATE</text>
                <text x="388" y="232" fontSize="9" fontFamily="JetBrains Mono" fill="#A19A9D" opacity="0.5" textAnchor="end">PROJECTED RESPONSE →</text>
              </svg>
              <p data-testid="xps-scenario-note" className="mt-4 max-w-xl text-base leading-relaxed text-fog">{SCEN.find((s) => s.id === scen).note}</p>
            </div>
          </R>
        </div>
      </section>

      {/* 06 LOOP · scroll-driven highlight */}
      <LoopScrollTrack items={LOOP} />

      {/* 07 FINAL CTA */}
      <section data-testid="xps-final" className="bg-ink-2/60 px-6 py-28 text-center md:px-12 md:py-36">
        <R>
          <p className="font-mono text-[11px] tracking-[0.28em] text-white/40">07 · GET TRIFIT XPS</p>
          <h2 data-testid="xps-final-headline" className="mx-auto mt-8 max-w-3xl font-display text-4xl font-light leading-tight text-white sm:text-5xl lg:text-6xl">The intelligence behind winning <span className="text-signal">decisions</span>.</h2>
          <p className="mx-auto mt-6 max-w-lg text-base leading-relaxed text-white/60">Understand. Anticipate. Align.</p>
          <a href={XPS_URL} target="_blank" rel="noopener noreferrer" data-testid="xps-final-cta" className="mt-10 inline-block bg-white px-9 py-4 font-mono text-[11px] tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-signal hover:text-white">GET TRIFIT XPS</a>
        </R>
      </section>
    </main>
  );
};

export default TrifitXps;
